import {BlindsState, ClockState, DelierState, MoodState, WhiteBoardState} from "@/store/store";
import ContentService from "@/services/ContentService";
import Router from 'vue-router';
import LocaleService from "@/services/LocaleService";
import {EndVideoCatalog, EndVideoInfo} from "@/store/ContentTypes";
import {Vue} from "vue/types/vue";
import {Store} from "vuex";

const SHOW_ALL_VIDEOS:boolean = true;
type VueElement = Vue | Element | Vue[] | Element[];

interface StringMap {
    [name: string]: string;
}
interface Dict {
    [name: string]: any;
}
interface IPos {
    x: number;
    y: number;
}

export enum MenuType {
    Nurse = 'nurse',
    Computer = 'computer',
    Patient = 'patient'
}

export enum PatientFeedbackState { InActief = 'inactief', Actief = 'actief', Genegeerd = 'genegeerd' }

export class Hotspot {
    public name:string = '';
    public icon:string = '';
    public x:number = 0.0;
    public y:number = 0.0;
    public actions:HotspotOption[] = [];
    public hasConfirm:boolean = false;
    public orientation:string = "left"; // left, right, up, upleft

    public static NONE:Hotspot = <Hotspot> { name: 'NONE' };
}
export class HotspotOption {    // maps precies op de content in de json map
    public name:string = '';
    public enabledForModelStates:number[] = [];
    public targetModelState:number = -1;
    public scores:HotspotOptionScore[] = [];
    public weight:number = 0;

    public static NONE:HotspotOption = <HotspotOption> { name: 'NONE' };
}
export class HotspotOptionScore {
    public forPatientState:number= 0;
    public value:number = 0;
}
export class HotspotOptionRecord {
    public hotspot:Hotspot = Hotspot.NONE;
    public option:HotspotOption = HotspotOption.NONE;
    public deltaScore:number = 0;
    public isMissedFeedback:boolean = false;
    public patientFeedbackState:string = PatientFeedbackState.InActief;
}
export class FeedbackRecord {
    public isMissedFeedback:boolean = false;
    public deltaScore:number = 0;
    public text:string = '';
}
export enum VideoMarkerType { Message = "message", Tip = "tip" }
export class VideoMarker {
    public time:number = 0;
    public type:string = VideoMarkerType.Message;
    public param:any = undefined;
}
export class VideoMarkers {
    public files:string[] = [];
    public markers:VideoMarker[] = [];
}
export default class GameLogic {

    public static dayFromSituation(situation: number): number {
        return [0, 1, 2, 2, 2, 3, 3, 3][situation];
    }

    public static situationFromDayAndMood(day: number, mood: MoodState): number {
        switch (mood) {
            case MoodState.wit:
            case MoodState.groen:
                return [0, 1, 2, 5][day];
            case MoodState.oranje:
                return [0, 1, 3, 6][day];
            case MoodState.rood:
            case MoodState.zwart:
                return [0, 1, 4, 7][day];
        }
    }

    public static getSituationObject(state: DelierState): any {
        const situationIndex = this.situationFromDayAndMood(state.day, state.mood);
        const situationObject = ContentService.situations[situationIndex];
        return situationObject;
    }

    public static getRoomImage(state: DelierState): string {
        const situationObject = this.getSituationObject(state);
        if (state.blinds == BlindsState.opened) {
            return situationObject.situation.room.backgroundImage.blindsOpenUrl;
        }
        else {
            return situationObject.situation.room.backgroundImage.blindsClosedUrl;
        }
    }

    public static getClockImage(state: DelierState): string {
        const situationObject = this.getSituationObject(state);
        if (state.clock == ClockState.removed) {
            return '';
        } else if (state.blinds == BlindsState.opened) {
            return situationObject.situation.room.clock.blindsOpenUrl;
        }
        else {
            return situationObject.situation.room.clock.blindsClosedUrl;
        }
    }

    public static getWhiteboardImage(state: DelierState): string {
        if (state.whiteBoard == WhiteBoardState.empty) {
            return '';
        } else {
            return ['/', state.locale, '/images/', ContentService.localized.roomImages.whiteboard[state.whiteBoard - 1]].join('');
        }
    }

    public static getCurrentActions(state: DelierState, type: MenuType): any[] {
        //console.log('getCurrentActions', type);
        const situationObject = this.getSituationObject(state);
        switch (type) {
            case MenuType.Computer:
                return situationObject.actions.mobile.options;
            case MenuType.Nurse:
                return situationObject.actions.items.filter((item: any) => item.name != 'communiceren');
            case MenuType.Patient:
                return situationObject.actions.items.filter((item: any) => item.name == 'communiceren');
            default:
                //console.log('[ERROR] getCurrentActions: unknown menu type', type);
                return [];
        }
    }

    static hotspotFromContent(state: DelierState, hotspotInfo: any, hasConfirm: boolean): Hotspot {
        const actionBudget: number = state.dayState.turnsLeft;
        const situationObject = this.getSituationObject(state);

        const modelState: number = this.getModelState(state, hotspotInfo.name);
        let mapping: Dict = <Dict> ContentService.hotspotInfo;
        let info: any = mapping[hotspotInfo.name];
        const button = <Hotspot>{
            name: hotspotInfo.name,
            icon: info ? info.icon : undefined,
            orientation: info ? info.orientation : undefined,
            actions: hotspotInfo.actions
                /*.map((action: any) => <HotspotOption>action)*/
                .filter((action: HotspotOption) =>
                    action.name !== 'default' &&
                    action.weight <= actionBudget &&
                    !this.isOptionDone(state, action) && (
                    action.enabledForModelStates == undefined
                    || action.enabledForModelStates.indexOf(modelState) >= 0)),
            x: 0.0,
            y: 0.0,
            hasConfirm: hasConfirm
        };
        if (undefined == button.icon) {
            button.icon = hotspotInfo.name;
        }
        const icon = situationObject.room.icons.find((icon: any) => icon.name == button.icon);
        if (icon != undefined) {
            button.x = icon.x;
            button.y = icon.y;
        }
        return button;
    }

    public static getHotspotByName(state: DelierState, name: string): Hotspot {
        const situationObject = this.getSituationObject(state);
        let content: any = situationObject.actions.items.find((item: any) => item.name == name);
        return this.hotspotFromContent(state, content, true);
    }

    public static getComputerButtonByName(state: DelierState, name: string): Hotspot {
        const situationObject = this.getSituationObject(state);
        //console.log(situationObject.actions.mobile.options, name);
        let content: any = situationObject.actions.mobile.options.find((item: any) => item.name === name);
        if (content === undefined) return Hotspot.NONE;
        return this.hotspotFromContent(state, content, true);
    }

    public static getHotspots(state: DelierState, actions: any[], hasConfirm: boolean): Hotspot[] {
        //const situationObject = this.getSituationObject(state);
        const result = [];
        //const actionBudget: number = state.dayState.turnsLeft;

        for (let item of actions) {
            const button = this.hotspotFromContent(state, item, hasConfirm);
            result.push(button);
        }
        return result;
    }

    public static getOptionScoreForPatientState(modelState: number, option: HotspotOption): number {
        for (let entry of option.scores) {
            if (entry.forPatientState === modelState) {
                return entry.value;
            }
        }
        return 0; // invalid
    }

    public static isHotspotDone(state: DelierState, name: String): boolean {
        for (let entry of state.dayState.log) {
            if (entry.hotspot.name === name) {
                return true;
            }
        }
        return false;
    }

    public static isOptionDone(state: DelierState, option: HotspotOption): boolean {
        for (let entry of state.dayState.log) {
            if (entry.option === option) {
                return true;
            }
        }
        return false;
    }

    // public static findComputerButtonInfo(state:DelierState, name:string):any {
    //     const situation = GameLogic.getSituationObject(state);
    //     const infos = situation.actions.mobile.options;
    //     for(let info of infos) {
    //         if (info.name === name) {
    //             return info;
    //         }
    //     }
    // }

    // // info : moet verwijzen naar content in bijv ContentService.situations[0].actions.items[0]
    // public static getValidInfoOptions(state:DelierState, info:any):HotspotOption[] {
    //     const turnsLeft:number = state.dayState.turnsLeft;
    //     return info.actions.filter((option:any) =>
    //         option.name != 'default' &&
    //         option.weight <= turnsLeft &&
    //         !this.isOptionDone(state, option));
    // }

    public static getValidHotspotOptions(state: DelierState, hotspot: Hotspot): HotspotOption[] {
        return hotspot.actions.filter(option => option.name != 'default' && !this.isOptionDone(state, option));
    }

    public static getIntroVideo(state: DelierState): string {
        const situationObject = this.getSituationObject(state);
        return this.getVideoUrl(state, situationObject.situation.delirium.videoUrls);
    }

    public static getSlotVideo(state: DelierState): string {
        const situationObject = this.getSituationObject(state);
        return this.getVideoUrl(state, situationObject.situation.end.videoUrls);
    }

    static getVideoUrl(state: DelierState, urls: any[]): string {
        const videoObject = urls.find(
            (item: any) => item.blindsState == state.blinds && item.whiteboardState == state.whiteBoard
        );
        if (videoObject == undefined)
            return ['Missing intro video for this day with blind=',
                state.blinds, ' and whiteboard=',
                state.whiteBoard].join('');
        else {
            return this.processedVideoUrl(state, videoObject.url);
        }
    }

    public static getPatientVideoUrl(state: DelierState, optionName: any): string {
        const situation = this.getSituationObject(state);
        const media = situation.situation.mediaUrls.find((item: any) => item.actionName == optionName);
        //console.log('getPatientVideoUrl', media.value);
        if (media != undefined) {
            return this.processedVideoUrl(state, media.value);
        }
        else {
            return 'video ' + optionName + ' not found';
        }
    }

    static processedVideoUrl(state: DelierState, sourceUrl: string): string {
        let url: string = sourceUrl;
        url = url.replace(/^delier\d\//, "");
        url = url.replace('${locale}', [state.locale, "/video"].join(''));
        url = url.replace(".flv", ".mp4");
        return url;
    }

    public static getModelState(state: DelierState, name: string): number {
        switch (name) {
            case 'klok':
                return state.clock;
            case 'whiteboard':
                return state.whiteBoard;
            case 'lamellen':
                return state.blinds;
            case 'communiceren':
                return state.communicateState;
            case 'bril':
                return state.communicateState; // dit is geen typfout
            case 'ontbijt':
                return state.communicateState; // dit is geen typfout
            // case 'infuus':
            //     return state.infuusState;
            // case 'medicatietoedienen':
            //     return state.medicatieState;
            // case 'overleggenmetchirurg':
            //     return state.talkToSurgeonState;
            default:
                //console.log('[WARNING] getModelState unknown', name);
                return 0;
        }
    }

    public static setModelState(state: DelierState, name: string, value: number): void {
        switch (name) {
            case 'klok':
                state.clock = value;
                break;
            case 'whiteboard':
                state.whiteBoard = value;
                break;
            case 'lamellen':
                state.blinds = value;
                break;
            case 'communiceren':
                state.communicateState = value;
                break;
            // case 'bril':
            //     state.brilState = value;
            //     break;
            // case 'infuus':
            //     state.infuusState = value;
            //     break;
            // case 'medicatietoedienen':
            //     state.medicatieState = value;
            //     break;
            // case 'overleggenmetchirurg':
            //     state.talkToSurgeonState = value;
            //     break;
            // case 'ontbijt':
            //     state.ontbijtState = value;
            //     break;
            default:
                console.log('cannot set model state', name, ' to ', value);
        }
    }

    public static executeOption(state: DelierState, hotspot: Hotspot, option: HotspotOption): void {
        let deltaScore: number = 0;
        let patientState:number = this.getPatientState(state);
        state.dayState.turnsLeft -= option.weight;
        if (option.targetModelState >= 0) {
            GameLogic.setModelState(state, hotspot.name, option.targetModelState)
        }
        if (option.name === "patientnegeren") {
            state.dayState.patientIgnored = true;
        }
        for(let item of option.scores) {
            if (item.forPatientState == patientState) {
                state.dayState.score += item.value;
                deltaScore += item.value;
            }
        }
        const record: HotspotOptionRecord = <HotspotOptionRecord> {
            deltaScore: deltaScore,
            hotspot: hotspot,
            option: option,
            patientFeedbackState: this.getPatientFeedbackState(state)
        };
        this.playOptionAudio(state, hotspot, option);

        console.log('executeOption', hotspot.name, option.name, 'score=', deltaScore, 'patientState=', patientState);

        state.dayState.log.push(record);
    }

    static playOptionAudio(state: DelierState, hotspot: Hotspot, option: HotspotOption): void {
        let categoryName: string = hotspot.name;
        let actionName: string = option.name;
        for (let entry of ContentService.sounds.sounds) {
            if( entry.categoryName == categoryName && entry.actionName == actionName ) {
                let url:string = [state.locale, '/sounds/', entry.value].join('');
                var audio = new Audio(url);
                //console.log('playing audio', url);
                audio.play();
            }
        }
    }

    static getPatientFeedbackState(state:DelierState):string {
        return state.dayState.patientIgnored ?
            PatientFeedbackState.Genegeerd :
            (state.communicateState == 0 ? PatientFeedbackState.InActief : PatientFeedbackState.Actief);
    }

    public static deltaMood(state: DelierState, delta: number): void {
        var mood: number = state.mood + delta;
        if (mood < MoodState.groen) mood = MoodState.groen;
        if (mood > MoodState.rood) mood = MoodState.rood;
        state.mood = mood;
    }

    public static nextDay($router: Router, $store: any): void {
        const day: number = $store.state.day + 1;
        if (day < 4) {
            $store.state.day = day;
            $router.push('/morning/' + day);
            $store.commit('startDay');
        } else {
            //console.log('spel is afgelopen!');
            $router.push('/end');
        }
    }

    public static getPositionStyle(pos: IPos, pixelWidth: number, pixelHeight: number): any {
        return {
            position: 'absolute',
            left: (pos.x * 100 / pixelWidth) + '%',
            top: (pos.y * 100 / pixelHeight) + '%'
        }
    }

    public static getRoomIconPosition(state: DelierState, key: string): IPos {
        const situation: any = this.getSituationObject(state);
        let result = situation.room.icons.find((el: any) => el.name == key);
        return result;
    }

    public static getRoomObjectPosition(state: DelierState, key: string): IPos {
        const situation: any = this.getSituationObject(state);
        let result = situation.room.objects.find((el: any) => el.name == key);
        return result;
    }

    public static getSituationStartPosition(state: DelierState, key: string): IPos {
        const situation: any = this.getSituationObject(state);
        const dict: Dict = <Dict>situation.situation.delirium;
        return dict[key];
    }

    public static getSituationEndPosition(state: DelierState, key: string): IPos {
        const situation: any = this.getSituationObject(state);
        const dict: Dict = <Dict>situation.situation.end;
        return dict[key];
    }

    public static getHotspotImage(state: DelierState, hotspot: Hotspot): string {
        const hotspotImages: StringMap = <StringMap> ContentService.hotspotImages;
        let imageUrl = hotspotImages[hotspot.name];
        if (hotspot.name == 'whiteboard' && state.whiteBoard != WhiteBoardState.empty) {
            imageUrl = [
                '/',
                state.locale,
                '/images/',
                ContentService.localized.hotspotImages.whiteboard[state.whiteBoard - 1]
            ].join('');
        }
        else {
            imageUrl = '/images/ui/' + imageUrl;
        }
        return imageUrl;
    }

    public static getPatientState(state:DelierState):number {
        //return state.dayState.talkedToPatientState == 0 ? 0 : 1;
        return state.communicateState == 0 ? 0 : 1;
    }

    // modifies state
    public static processDayFeedback(state: DelierState):void {
        const log:FeedbackRecord[] = []; // state.dayState.log.filter(record => record.deltaScore != 0);
        const situation = GameLogic.getSituationObject(state);
        const patientState:number = this.getPatientState(state);
        let allHotspots = situation.actions.items.concat(situation.actions.mobile.options);
        let endDayPatientFeedbackState:string = this.getPatientFeedbackState(state); // state.dayState.patientIgnored ? 'genegeerd' : (patientState == 0 ? 'inactief' : 'actief');

        for (let hotspot of allHotspots) {

            if( state.clock == ClockState.working && hotspot.name == "klok" ) {
                // klok state word bewaard van dag naar dag, maar we staan de speler wel toe een werkende klok te verwijderen
                continue;
            }

            let isDone = false;
            for (let entry of state.dayState.log) {
                if (entry.hotspot.name === hotspot.name) {
                    if( entry.deltaScore < 0 ) {
                        let tip:string = LocaleService.tipKey(entry.hotspot.name, entry.option.name);
                        console.log("tip:", tip);
                        state.endTips[tip] = true;
                    }
                    log.push(GameLogic.getFeedbackRecord(
                        state,
                        entry.hotspot.name,
                        entry.option,
                        entry.patientFeedbackState,
                        entry.deltaScore
                    ));
                    isDone = true;
                }
            }
            if (!isDone) {
                for (let option of hotspot.actions) {
                    let optionScore: number = GameLogic.getOptionScoreForPatientState(patientState, option);
                    if (optionScore !== 0 && option.name === 'default') {
                        if( optionScore < 0 ) {
                            let tip: string = LocaleService.tipKey(hotspot.name, option.name);
                            console.log("tip:", tip);
                            state.endTips[tip] = true;
                        }
                        log.push(GameLogic.getFeedbackRecord(
                            state,
                            hotspot.name,
                            option,
                            endDayPatientFeedbackState,
                            optionScore
                        ));
                        state.dayState.score += optionScore;
                    }
                }
            }
        }
        state.dayState.feedback = log;
    }

    public static processEndFeedback(state:DelierState):EndVideoInfo[] {
        let result: EndVideoInfo[] = [];
        let catalog: EndVideoCatalog = <EndVideoCatalog> (ContentService.endVideos[state.locale]);
        if( SHOW_ALL_VIDEOS ) {
            result = catalog.videos;
        }
        else {
            console.log(state.endTips);
            for (let item of catalog.videos) {
                console.log(item.id, state.endTips[item.id]);
                if (state.endTips[item.id]) {
                    result.push(item);
                }
            }
        }
        result = result.concat(catalog.defaultVideos);
        return result;
    }

    public static getFeedbackRecord(state: DelierState, hotspotName: string, option: HotspotOption, patientFeedbackState: string, deltaScore: number): FeedbackRecord {
        const patientState: number = this.getPatientState(state);
        let localeKey: string = ['acties.actie', hotspotName, option.name, 'feedback', patientFeedbackState].join('.');
        let feedbackText: string = LocaleService.getValue(state, localeKey);
        return <FeedbackRecord>{
            isMissedFeedback: false,
            deltaScore: deltaScore,
            text: feedbackText
        };
    }

    public static hasSeenTip(state:DelierState, key:string):boolean {
        for( let tip of state.tutorialState.tipHistory ) {
            if( tip.key === key ) return true;
        }
        return false;
    }

    public static executeRandomTestRun($store: Store<DelierState>):string[] {
        let results:string[] = [];
        let state:DelierState = $store.state;

        $store.commit("resetGame");
        for( let day:number = 0; day < 4; ++ day )
        {
            state.day = day;
            $store.commit("startDay");

            results.push( ['dag ', day + 1, ': ', MoodState[state.mood]].join(''));

            let moveCount:number = 1 + 10 * Math.random();
            for( let i:number = 0; i < moveCount; ++i )
            {
                const actions = GameLogic.getCurrentActions(state, MenuType.Nurse)
                    .concat(GameLogic.getCurrentActions(state, MenuType.Computer))
                    .concat(GameLogic.getCurrentActions(state, MenuType.Patient));
                const hotspots = GameLogic.getHotspots(state, actions, false);
                //console.log(hotspots);

                console.assert(hotspots.length > 0, 'no hotspots!');
                let hotspotIndex:number = Math.floor(Math.random() * hotspots.length);
                //console.log(hotspotIndex);
                const hotspot:Hotspot = hotspots[hotspotIndex];
                const validActions = hotspot.actions; // GameLogic.getValidHotspotOptions($store.state, hotspot);

                //console.log('hotspot', hotspot.name, 'actions:', hotspot.actions, 'valid:', validActions);

                if( validActions.length > 0)
                {
                    const actionIndex:number = Math.floor(Math.random() * validActions.length);
                    const action:HotspotOption = validActions[actionIndex];
                    results.push([hotspot.name, ' / ', action.name].join(''));
                    GameLogic.executeOption(state, hotspot, action);
                }
            }

            GameLogic.testRun_endDay($store, results);
        }

        GameLogic.testRun_endGame(state, results);

        return results;
    }

    static testRun_endDay($store: Store<DelierState>, results:string[]):void {
        let state:DelierState = $store.state;
        $store.commit('processPatientMood');
        GameLogic.processDayFeedback(state);
        results.push(['dag score: ', state.dayState.score].join(''));
        //results.push(['patient\'s delier: ', MoodState[state.mood]].join(''));
        for (let record of state.dayState.feedback) {
            results.push(['. . ', record.deltaScore, ': ', record.text].join(''));
        }
    }

    static testRun_endGame(state:DelierState, results:string[]):void {
        state.endFeedback = GameLogic.processEndFeedback(state);

        for (let tipId in state.endTips) {
            results.push(['tip: ', tipId].join(''));
        }
    }

    static testRun_action(state:DelierState, results:string[], hotspotName:string, actionName:string ):void {
        const hotspot:Hotspot = GameLogic.getHotspotByName(state, hotspotName);
        for(let action of hotspot.actions) {
            if( action.name == actionName ) {
                results.push([hotspot.name, ' / ', action.name].join(''));
                GameLogic.executeOption(state, hotspot, action);
                break;
            }
        }
    }

    public static executeTestRun($store: Store<DelierState>, key:string):string[] {
        let results:string[] = [];
        let state:DelierState = $store.state;
        $store.commit("resetGame");

        if( key == 'klok') {
            // deze test een fout, waar je de batterij vervangt en later toch feedback krijgt,
            // dat je de klok had moeten vervangen
            state.day = 1;
            $store.commit("startDay");
            GameLogic.testRun_action(state, results, 'klok', 'batterijvervangen');
            GameLogic.testRun_endDay($store, results);
            state.day = 2;
            $store.commit("startDay");
            GameLogic.testRun_endDay($store, results);
        }

        GameLogic.testRun_endGame(state, results);
        return results;
    }
}